import type {
  CommercialOutcome,
  ModificationClassification,
  ModificationEntryType,
  ModificationRound,
  ModificationRoundState,
  ModificationWaitingFor,
  OpsFeasibilityVerdict,
  OpsScopeImpact,
} from '@/services/inspection-modification.service';

/**
 * D-131 — the pure client mirror of `modification-state-machine.ts`.
 *
 * This file decides only what a screen SHOWS. It never decides what a user is
 * ALLOWED to do: every action here is re-checked by the backend state machine
 * (409 on a stale state) and by `PermissionGuard` (403). Hiding a control is a
 * UX courtesy; the server is the boundary.
 *
 * Vocabulary is the product's, not the database's: a user reads "Awaiting
 * Operations feasibility review", never `OPS_REVIEW`.
 */

export const OPEN_ROUND_STATES: readonly ModificationRoundState[] = [
  'SUBMITTED',
  'OPS_REVIEW',
  'AWAITING_CUSTOMER_CLARIFICATION',
  'COMMERCIAL_DECISION',
  'AWAITING_CUSTOMER_DECISION',
];

export function isOpenRoundState(state: ModificationRoundState): boolean {
  return OPEN_ROUND_STATES.includes(state);
}

/** What the round is doing right now, in words a user recognises. */
export const ROUND_STATE_LABELS: Record<ModificationRoundState, string> = {
  SUBMITTED: 'New request — with Commercial',
  OPS_REVIEW: 'Awaiting Operations feasibility review',
  AWAITING_CUSTOMER_CLARIFICATION: "Waiting for the customer's answer",
  COMMERCIAL_DECISION: 'With Commercial for a decision',
  AWAITING_CUSTOMER_DECISION: 'Revised quotation with the customer',
  CLOSED_ACCEPTED_NO_CHANGE: 'Closed — accepted, no quotation change',
  CLOSED_ACCEPTED: 'Closed — revised quotation accepted',
  CLOSED_REJECTED: 'Closed — revised quotation rejected',
  CLOSED_DECLINED: 'Closed — declined by Commercial',
  CLOSED_NOT_FEASIBLE: 'Closed — not feasible',
  CLOSED_SUPERSEDED_BY_ROUND: 'Closed — replaced by a newer request',
};

/** Badge tone per state; paired with the label text, never colour alone. */
export const ROUND_STATE_BADGE: Record<ModificationRoundState, string> = {
  SUBMITTED: 'bg-sky-500/10 text-sky-700',
  OPS_REVIEW: 'bg-amber-500/10 text-amber-700',
  AWAITING_CUSTOMER_CLARIFICATION: 'bg-indigo-500/10 text-indigo-700',
  COMMERCIAL_DECISION: 'bg-purple-500/10 text-purple-700',
  AWAITING_CUSTOMER_DECISION: 'bg-indigo-500/10 text-indigo-700',
  CLOSED_ACCEPTED_NO_CHANGE: 'bg-green-500/10 text-green-700',
  CLOSED_ACCEPTED: 'bg-green-500/10 text-green-700',
  CLOSED_REJECTED: 'bg-red-500/10 text-red-700',
  CLOSED_DECLINED: 'bg-slate-500/10 text-slate-600',
  CLOSED_NOT_FEASIBLE: 'bg-red-500/10 text-red-700',
  CLOSED_SUPERSEDED_BY_ROUND: 'bg-slate-500/10 text-slate-600',
};

export const WAITING_FOR_LABELS: Record<ModificationWaitingFor, string> = {
  COMMERCIAL: 'Commercial',
  OPERATIONS: 'Operations',
  CUSTOMER: 'the customer',
};

export const CLASSIFICATION_LABELS: Record<ModificationClassification, string> = {
  TECHNICAL: 'Technical',
  COMMERCIAL: 'Commercial',
  MIXED: 'Technical and commercial',
  UNCLEAR: 'Needs clarification',
};

export const OPS_VERDICT_LABELS: Record<OpsFeasibilityVerdict, string> = {
  FEASIBLE: 'Feasible',
  FEASIBLE_WITH_ALTERNATIVE: 'Feasible with an alternative',
  NOT_FEASIBLE: 'Not feasible',
  MORE_INFORMATION_REQUIRED: 'Need more information from the customer',
};

/** One line under each verdict so the choice is obvious without training. */
export const OPS_VERDICT_HINTS: Record<OpsFeasibilityVerdict, string> = {
  FEASIBLE: 'The change can be done as the customer asked.',
  FEASIBLE_WITH_ALTERNATIVE: 'Not as asked, but describe what would work instead.',
  NOT_FEASIBLE: 'The change cannot be done. Explain why.',
  MORE_INFORMATION_REQUIRED:
    'You cannot answer yet. Commercial will relay your question to the customer.',
};

export const OPS_VERDICTS: readonly OpsFeasibilityVerdict[] = [
  'FEASIBLE',
  'FEASIBLE_WITH_ALTERNATIVE',
  'NOT_FEASIBLE',
  'MORE_INFORMATION_REQUIRED',
];

export const SCOPE_IMPACT_LABELS: Record<OpsScopeImpact, string> = {
  NONE: 'No change to the scope of work',
  REDUCED: 'Scope of work is reduced',
  INCREASED: 'Scope of work is increased',
  CHANGED: 'Scope of work changes',
};

export const SCOPE_IMPACTS: readonly OpsScopeImpact[] = ['NONE', 'REDUCED', 'INCREASED', 'CHANGED'];

export const COMMERCIAL_OUTCOME_LABELS: Record<CommercialOutcome, string> = {
  ACCEPTED_NO_CHANGE: 'Accepted — no change to the quotation',
  DECLINED: 'Declined',
  NOT_FEASIBLE: 'Not feasible',
  ACCEPTED_WITH_REVISION: 'Accepted — revised quotation issued',
  ALTERNATIVE_OFFERED: 'Alternative offered',
};

export const ENTRY_TYPE_LABELS: Record<ModificationEntryType, string> = {
  REQUESTED: 'Customer requested a modification',
  ADDED: 'Customer added to the request',
  TRIAGED: 'Commercial classified the request',
  REFERRED_TO_OPS: 'Referred to Operations',
  OPS_VERDICT: 'Operations feasibility response',
  CLARIFICATION_REQUESTED: 'Question sent to the customer',
  CUSTOMER_REPLIED: 'Customer replied',
  INTERNAL_NOTE: 'Internal note',
  NOTE_PUBLISHED: 'Operations note shared with the customer',
  REVISION_SENT: 'Revised quotation sent',
  CUSTOMER_DECIDED: 'Customer decided on the quotation',
  CLOSED: 'Request closed',
};

/**
 * Which conditional fields a verdict needs — the same rule as the server's
 * `opsVerdictRequires()`; the server re-validates.
 */
export function opsVerdictRequires(verdict: OpsFeasibilityVerdict): {
  alternativeDescription: boolean;
  clarificationQuestion: boolean;
} {
  return {
    alternativeDescription: verdict === 'FEASIBLE_WITH_ALTERNATIVE',
    clarificationQuestion: verdict === 'MORE_INFORMATION_REQUIRED',
  };
}

/**
 * The Operations-side reading of a round. Five situations, and the screen
 * shows exactly one of them:
 *
 *   NO_ROUND               nothing to review
 *   AWAITING_OPERATIONS    the round is on Operations' desk — the only actionable case
 *   OPERATIONS_RESPONDED   Operations has answered; the round is open elsewhere
 *   WITH_OTHERS            open, not yet (or no longer) an Operations question
 *   CLOSED                 finished; history only
 *
 * A re-referral (T13) or a customer answer to an Operations question (T7 back to
 * OPS_REVIEW) makes a round with an earlier verdict actionable again, so the
 * state wins over the presence of a verdict.
 */
export type OperationsRoundView =
  'NO_ROUND' | 'AWAITING_OPERATIONS' | 'OPERATIONS_RESPONDED' | 'WITH_OTHERS' | 'CLOSED';

export function operationsViewOf(round: ModificationRound | null | undefined): OperationsRoundView {
  if (!round) return 'NO_ROUND';
  if (!isOpenRoundState(round.state)) return 'CLOSED';
  if (round.state === 'OPS_REVIEW') return 'AWAITING_OPERATIONS';
  return round.opsVerdict ? 'OPERATIONS_RESPONDED' : 'WITH_OTHERS';
}

/**
 * Whether the round is waiting on Operations' answer. Mirrors the machine's
 * precondition for `OPS_VERDICT` (state must be `OPS_REVIEW`).
 */
export function canRecordOpsVerdict(round: ModificationRound | null | undefined): boolean {
  return operationsViewOf(round) === 'AWAITING_OPERATIONS';
}

/**
 * One sentence telling Operations where an open round is and whether it will
 * come back to them — so "still actionable?" never needs a state name.
 */
export function describeWhereRoundIs(round: ModificationRound): string {
  switch (round.state) {
    case 'SUBMITTED':
      return 'Commercial is reviewing the request and will refer it here if a feasibility review is needed.';
    case 'OPS_REVIEW':
      return 'Commercial has asked Operations whether this change is technically possible and what it would change.';
    case 'AWAITING_CUSTOMER_CLARIFICATION':
      return round.clarificationReturnState === 'OPS_REVIEW'
        ? 'Waiting for the customer to answer the Operations question. The request returns here when they reply.'
        : 'Commercial has asked the customer a question and is waiting for the answer.';
    case 'COMMERCIAL_DECISION':
      return 'Commercial is deciding how to respond to the customer. No Operations action is needed.';
    case 'AWAITING_CUSTOMER_DECISION':
      return 'A revised quotation is with the customer for a decision.';
    default:
      return 'This request is closed.';
  }
}

/** The most recent round, the one whose state matters; `null` when none. */
export function currentRoundOf(rounds: readonly ModificationRound[]): ModificationRound | null {
  if (rounds.length === 0) return null;
  return rounds.reduce((best, r) => (r.roundNumber > best.roundNumber ? r : best), rounds[0]);
}

// ── Commercial (D-131 M6) ────────────────────────────────────────────────────

/**
 * The Commercial-side reading of a round — one per state group, so the
 * Modification panel shows exactly one zone of controls (design §8.1, §14.2):
 *
 *   NO_ROUND                     nothing to work
 *   TRIAGE                       SUBMITTED — classify and route
 *   WITH_OPERATIONS              OPS_REVIEW — waiting on feasibility; notes only
 *   AWAITING_CUSTOMER_ANSWER     the customer owes a reply; notes only
 *   DECISION                     COMMERCIAL_DECISION — close, ask, re-refer or send
 *   AWAITING_CUSTOMER_DECISION   a revision is with the customer; notes only
 *   CLOSED                       history
 */
export type CommercialRoundView =
  | 'NO_ROUND'
  | 'TRIAGE'
  | 'WITH_OPERATIONS'
  | 'AWAITING_CUSTOMER_ANSWER'
  | 'DECISION'
  | 'AWAITING_CUSTOMER_DECISION'
  | 'CLOSED';

export function commercialViewOf(round: ModificationRound | null | undefined): CommercialRoundView {
  if (!round) return 'NO_ROUND';
  switch (round.state) {
    case 'SUBMITTED':
      return 'TRIAGE';
    case 'OPS_REVIEW':
      return 'WITH_OPERATIONS';
    case 'AWAITING_CUSTOMER_CLARIFICATION':
      return 'AWAITING_CUSTOMER_ANSWER';
    case 'COMMERCIAL_DECISION':
      return 'DECISION';
    case 'AWAITING_CUSTOMER_DECISION':
      return 'AWAITING_CUSTOMER_DECISION';
    default:
      return 'CLOSED';
  }
}

export const CLASSIFICATIONS: readonly ModificationClassification[] = [
  'TECHNICAL',
  'COMMERCIAL',
  'MIXED',
  'UNCLEAR',
];

/** Where each classification sends the round — mirrors T2 / T3 / T4. */
export const CLASSIFICATION_HINTS: Record<ModificationClassification, string> = {
  TECHNICAL: 'Operations checks whether it can be done.',
  COMMERCIAL: 'No technical question — Commercial decides directly.',
  MIXED: 'Operations checks feasibility first, then Commercial decides.',
  UNCLEAR: 'Ask the customer to explain before routing.',
};

/** Does this classification need a question for the customer (T4)? */
export function triageRequiresQuestion(classification: ModificationClassification): boolean {
  return classification === 'UNCLEAR';
}

export type ClosureOutcome = 'ACCEPTED_NO_CHANGE' | 'DECLINED' | 'NOT_FEASIBLE';

export const CLOSURE_OUTCOMES: readonly ClosureOutcome[] = [
  'ACCEPTED_NO_CHANGE',
  'DECLINED',
  'NOT_FEASIBLE',
];

export const CLOSURE_OUTCOME_HINTS: Record<ClosureOutcome, string> = {
  ACCEPTED_NO_CHANGE:
    'Agreed, and the quotation does not change. The customer keeps the current quotation to approve.',
  DECLINED:
    'Not accommodated on commercial grounds. The current quotation stays available to the customer.',
  NOT_FEASIBLE:
    'Operations found it cannot be done. Requires a matching Operations verdict on this round.',
};

/**
 * Which closures the server will accept right now — the same precondition as
 * the state machine's `CLOSE`: `NOT_FEASIBLE` needs a matching Operations
 * verdict on THIS round. Everything is re-checked server-side (409).
 */
export function closureOutcomesFor(round: ModificationRound): readonly ClosureOutcome[] {
  return CLOSURE_OUTCOMES.filter(
    (o) => o !== 'NOT_FEASIBLE' || round.opsVerdict === 'NOT_FEASIBLE',
  );
}

/**
 * The design's "Next action" sentence (§14.2): one line derived from the state
 * and the Operations verdict, so Commercial never reads a state name to know
 * what to do.
 */
export function nextActionFor(round: ModificationRound): string {
  switch (round.state) {
    case 'SUBMITTED':
      return 'Classify the request: does it need an Operations feasibility check, or can Commercial answer directly?';
    case 'OPS_REVIEW':
      return 'Operations is assessing feasibility. No Commercial action until they respond.';
    case 'AWAITING_CUSTOMER_CLARIFICATION':
      return round.clarificationReturnState === 'OPS_REVIEW'
        ? 'Waiting for the customer to answer the Operations question; the round returns to Operations when they reply.'
        : 'Waiting for the customer to answer your question.';
    case 'COMMERCIAL_DECISION':
      switch (round.opsVerdict) {
        case 'NOT_FEASIBLE':
          return 'Operations found this cannot be done — close with "Not feasible" and tell the customer, or refer back to Operations if you disagree.';
        case 'FEASIBLE_WITH_ALTERNATIVE':
          return 'Operations proposes an alternative — put it to the customer, or send a revised quotation if the ERP quotation changes.';
        case 'FEASIBLE':
          return round.opsScopeImpact && round.opsScopeImpact !== 'NONE'
            ? 'Operations confirmed feasible with a change to the scope of work — decide whether the ERP quotation is reissued (send a revision) or close with an answer.'
            : 'Operations confirmed feasible with no scope change — you can close this with an answer; no new quotation is needed.';
        default:
          return 'Decide: close with an answer (no new quotation), ask the customer a question, refer to Operations, or send a revised quotation.';
      }
    case 'AWAITING_CUSTOMER_DECISION':
      return 'A revised quotation is with the customer. The round closes on their decision.';
    default:
      return 'This request is closed.';
  }
}

/** Whole days since `iso`, never negative. */
export function ageDays(iso: string, now: Date = new Date()): number {
  const ms = now.getTime() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

/** Design §13.2: the current revision is the highest-numbered SENT / ACCEPTED / REJECTED one. */
export function currentRevisionOf(
  revisions: readonly { revisionNumber: number; status: string }[],
): { revisionNumber: number; status: string } | null {
  let best: { revisionNumber: number; status: string } | null = null;
  for (const r of revisions) {
    if (!['SENT', 'ACCEPTED', 'REJECTED'].includes(r.status)) continue;
    if (!best || r.revisionNumber > best.revisionNumber) best = r;
  }
  return best;
}
